// Data Sources Tab - Reference mapping of all dashboard data to their sources
//
// Shows where each UI field/metric comes from (database table, external system, etc.)
// so admins can verify correctness and trace data lineage.

import styles from './DataSourcesTab.module.css';

interface DataMapping {
  uiField: string;
  source: string;
  table: string;
  notes?: string;
}

interface DataSection {
  title: string;
  description: string;
  mappings: DataMapping[];
}

const DATA_SECTIONS: DataSection[] = [
  {
    title: 'Customer 360 - Core Info',
    description: 'Customer billing/account data originating from Sage 100 ERP. Sage → IIS Sync → MySQL (chemmas_iq_sage) → PostgreSQL. The address here is the BILLING address, not shipping. Emails are typically billing/invoice contacts.',
    mappings: [
      { uiField: 'Customer Name', source: 'Sage AR_Customer', table: 'customers', notes: 'Sage 100 → IIS → MySQL → PostgreSQL' },
      { uiField: 'Address / City / State', source: 'Sage AR_Customer', table: 'customers', notes: 'This is the BILLING address, not shipping' },
      { uiField: 'Phone / Fax / Email', source: 'Sage AR_Customer', table: 'customers', notes: 'Usually billing/invoice contacts of the company' },
      { uiField: 'Order Email List', source: 'chemmas_sales', table: 'customers', notes: 'cp_email_list_shipping — order notification emails, configurable via customer portal (NOT from Sage AR_Customer)' },
      { uiField: 'Customer Status', source: 'Sage AR_Customer', table: 'customers', notes: 'A = Active, I = Inactive, etc.' },
      { uiField: 'Credit Limit / Credit Hold', source: 'Sage AR_Customer', table: 'customers' },
      { uiField: 'Current Balance', source: 'Sage AR_Customer', table: 'customers', notes: '0–30 day amount owed (NOT total balance — see AR Aging)' },
    ],
  },
  {
    title: 'Customer 360 - Sales Rep (Rep Field)',
    description:
      'The "Sales Rep" shown on customer pages is the ChemMasters salesperson assigned in Sage ERP, NOT the customer\'s own contact person.',
    mappings: [
      { uiField: 'Sales Rep Name', source: 'Sage AR_Salesperson', table: 'salespersons', notes: 'Linked via customers.salesperson_no → salespersons.sage_salesperson_no' },
      { uiField: 'Sage No. (e.g. "0001")', source: 'Sage AR_Salesperson', table: 'salespersons', notes: '4-character Sage identifier' },
      { uiField: 'Sales Rep Email', source: 'Sage AR_Salesperson', table: 'salespersons' },
      { uiField: 'Sales Rep Phone', source: 'Sage AR_Salesperson', table: 'salespersons' },
    ],
  },
  {
    title: 'Customer 360 - Enrichment',
    description: 'Additional customer data from the chemmas_sales MySQL database (Sales App).',
    mappings: [
      { uiField: 'Industry Type', source: 'chemmas_sales', table: 'sales_accounts', notes: 'AI-classified or manual' },
      { uiField: 'Customer Type', source: 'chemmas_sales', table: 'sales_accounts', notes: 'With classification reason' },
      { uiField: 'Primary Contact', source: 'chemmas_sales', table: 'sales_accounts', notes: 'Name, phone, email of main contact' },
      { uiField: 'Company Group', source: 'chemmas_sales', table: 'sales_accounts' },
      { uiField: 'Sphere Member', source: 'chemmas_sales', table: 'sales_accounts', notes: 'Boolean flag' },
    ],
  },
  {
    title: 'Customer 360 - Sales Metrics',
    description: 'YTD and prior year sales aggregated from Sage sales history invoices. Data flow: Sage 100 → IIS Sync → MySQL → PostgreSQL.',
    mappings: [
      { uiField: 'YTD Sales', source: 'Sage Sales History', table: 'sales_history', notes: 'SUM(extension_amt) for current calendar year' },
      { uiField: 'LYTD Sales', source: 'Sage Sales History', table: 'sales_history', notes: 'Same period last year' },
      { uiField: 'Last Year Total', source: 'Sage Sales History', table: 'sales_history', notes: 'Full prior year total' },
      { uiField: 'YTD vs LYTD %', source: 'Calculated', table: '—', notes: 'Derived: (YTD - LYTD) / LYTD × 100' },
      { uiField: 'Last Order Date', source: 'Sage Sales History', table: 'sales_history', notes: 'MAX(invoice_date)' },
    ],
  },
  {
    title: 'Customer 360 - Category Breakdown',
    description: 'Product category sales breakdown from sales metrics service.',
    mappings: [
      { uiField: 'Category', source: 'Sales Metrics Service', table: 'sales_history + products', notes: 'Grouped by product category from products table' },
      { uiField: 'Amount', source: 'Sales Metrics Service', table: 'sales_history', notes: 'SUM(extension_amt) per category' },
      { uiField: 'Percentage', source: 'Calculated', table: '—', notes: 'Category amount / total × 100' },
    ],
  },
  {
    title: 'Customer 360 - AR Aging',
    description: 'Accounts receivable aging buckets from Sage ERP. Total amount owed = CurrentBalance (0–30 days) + all aging categories. CurrentBalance alone is NOT the full balance.',
    mappings: [
      { uiField: 'Current Balance', source: 'Sage AR_Customer', table: 'customers', notes: '0–30 day amount only; total owed = this + all aging categories' },
      { uiField: 'Aging Category 1', source: 'Sage AR_Customer', table: 'customers', notes: '31–60 days overdue' },
      { uiField: 'Aging Category 2', source: 'Sage AR_Customer', table: 'customers', notes: '61–90 days overdue' },
      { uiField: 'Aging Category 3', source: 'Sage AR_Customer', table: 'customers', notes: '91–120 days overdue' },
      { uiField: 'Aging Category 4', source: 'Sage AR_Customer', table: 'customers', notes: '120+ days overdue' },
      { uiField: 'Avg Days to Pay', source: 'Sage AR_Customer', table: 'customers', notes: 'AvgDaysPaymentInvoice' },
      { uiField: 'Avg Days Overdue', source: 'Sage AR_Customer', table: 'customers', notes: 'AvgDaysOverDue' },
    ],
  },
  {
    title: 'Statistics - Quote Metrics',
    description: 'Quote win rates and pipeline metrics from sales quotes tables.',
    mappings: [
      { uiField: 'Quote Win Rate', source: 'chemmas_sales', table: 'sales_quotes', notes: 'Quotes matched to Sage orders (won_from_order = true)' },
      { uiField: 'Active Quotes', source: 'chemmas_sales', table: 'sales_quotes', notes: 'Status = "Open"' },
      { uiField: 'Pipeline (Won/Pending/Expired/Lost)', source: 'chemmas_sales', table: 'sales_quotes', notes: 'Grouped by status with SUM(quantity × price)' },
    ],
  },
  {
    title: 'Statistics - Revenue',
    description: 'Revenue figures from Sage sales history invoices.',
    mappings: [
      { uiField: 'Revenue', source: 'Sage Sales History', table: 'sales_history', notes: 'SUM(extension_amt) in date range' },
      { uiField: 'Revenue Change %', source: 'Calculated', table: '—', notes: 'Current period vs prior equivalent period' },
    ],
  },
  {
    title: 'Statistics - Email Classification',
    description: 'Email classification and sender matching metrics.',
    mappings: [
      { uiField: 'Total Processed', source: 'Email Pipeline', table: 'email_classification_results', notes: 'All classification attempts' },
      { uiField: 'Match Rate', source: 'Email Pipeline', table: 'email_classification_results', notes: 'MATCHED / total × 100' },
      { uiField: 'Classification Accuracy', source: 'Email Pipeline', table: 'email_classification_results', notes: 'MATCHED / (total - SKIPPED) × 100' },
      { uiField: 'Avg Response Time', source: 'Email Pipeline', table: 'email_classification_results + synced_emails_v2', notes: 'Time between email receipt and classification' },
    ],
  },
  {
    title: 'Statistics - Sales Rep Performance',
    description: 'Per-salesperson metrics from quotes joined to salesperson records.',
    mappings: [
      { uiField: 'Rep Name', source: 'chemmas_sales', table: 'sales_representatives', notes: 'From chemmas_sales.sales_rep' },
      { uiField: 'Quotes Count', source: 'chemmas_sales', table: 'sales_quotes', notes: 'COUNT per rep in date range' },
      { uiField: 'Win Rate', source: 'chemmas_sales', table: 'sales_quotes', notes: 'Won quotes / total quotes per rep' },
      { uiField: 'Revenue', source: 'Sage Sales History', table: 'sales_history', notes: 'SUM(extension_amt) per salesperson' },
    ],
  },
  {
    title: 'Orders & Invoices',
    description: 'Order management data from internal system and Sage invoices.',
    mappings: [
      { uiField: 'Order Status / Total', source: 'Internal', table: 'orders', notes: 'ChemMasters order management system' },
      { uiField: 'Line Items', source: 'Internal', table: 'order_line_items', notes: 'COUNT per order' },
      { uiField: 'Sage Order No.', source: 'Sage SO_SalesOrderHeader', table: 'orders', notes: 'Linked after Sage export' },
      { uiField: 'Invoice History', source: 'Sage Sales History', table: 'sales_history', notes: 'mvChatBotSales view in Sage' },
    ],
  },
  {
    title: 'Quotes & Special Pricing',
    description: 'Sales quotes and customer-specific pricing from chemmas_sales MySQL database (Sales App).',
    mappings: [
      { uiField: 'Quote Details', source: 'chemmas_sales', table: 'sales_quotes', notes: 'From chemmas_sales MySQL → PostgreSQL' },
      { uiField: 'Quote Products', source: 'chemmas_sales', table: 'sales_quote_products', notes: 'Line items with quantity × price' },
      { uiField: 'Special Pricing', source: 'chemmas_sales', table: 'customer_special_pricing', notes: 'Customer-specific discount agreements' },
      { uiField: 'Pricing Products', source: 'chemmas_sales', table: 'customer_special_pricing_products', notes: 'Individual product prices within agreements' },
    ],
  },
  {
    title: 'Customer 360 - Locations (Sales App)',
    description: 'Locations from chemmas_sales.customer_location. NOTE: These are Sales App-only locations and do NOT correspond to Sage ship-to addresses (see "Ship-To Addresses (Sage)" below). Key linkage: customer_number → location_id → contacts.',
    mappings: [
      { uiField: 'Location No.', source: 'chemmas_sales', table: 'customer_ship_locations', notes: 'From chemmas_sales.customer_location; links to contacts via location_id' },
      { uiField: 'Address / City / State', source: 'chemmas_sales', table: 'customer_ship_locations' },
      { uiField: 'Phone / Email', source: 'chemmas_sales', table: 'customer_ship_locations' },
      { uiField: 'Primary Flag', source: 'chemmas_sales', table: 'customer_ship_locations', notes: 'Boolean is_primary field' },
    ],
  },
  {
    title: 'Customer 360 - Ship-To Addresses (Sage)',
    description: 'Sage ERP SHIPPING addresses with operational data. These are the actual shipping destinations, as opposed to the billing address in the Core Info section above. Data flow: Sage 100 → IIS Sync → MySQL → PostgreSQL.',
    mappings: [
      { uiField: 'Ship-To Code / Name', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses', notes: 'Sage 100 → IIS → MySQL → PostgreSQL' },
      { uiField: 'Address / City / State / Zip', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses' },
      { uiField: 'Phone / Fax / Email', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses' },
      { uiField: 'Contact Code', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses', notes: 'Code only — actual contact names/details are in the separate sales_contacts table' },
      { uiField: 'Warehouse Code', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses', notes: 'Assigned warehouse for this location' },
      { uiField: 'Ship Via', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses', notes: 'Default shipping method' },
      { uiField: 'Tax Schedule', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses', notes: 'Tax configuration per location' },
      { uiField: 'Salesperson No.', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses', notes: 'Per-location salesperson (may differ from account-level)' },
      { uiField: 'Inactive Flag', source: 'Sage SO_ShipToAddress', table: 'ship_to_addresses', notes: 'UDF_INACTIVE = Y marks inactive locations' },
    ],
  },
  {
    title: 'Customer 360 - Contacts',
    description: 'Customer contacts from chemmas_sales.contact. Tied to the customer_location table — each location can have multiple contacts. These are sales/operational contacts used for Sales App purposes and customer portal login, distinct from the billing/invoice emails in Core Info.',
    mappings: [
      { uiField: 'Contact Name', source: 'chemmas_sales', table: 'sales_contacts', notes: 'From chemmas_sales.contact' },
      { uiField: 'Phone', source: 'chemmas_sales', table: 'sales_contacts' },
      { uiField: 'Email', source: 'chemmas_sales', table: 'sales_contacts', notes: 'Shipping/operational contacts (not billing)' },
      { uiField: 'Location ID', source: 'chemmas_sales', table: 'sales_contacts', notes: 'Links contact to customer_location; key relationship for location → contacts' },
    ],
  },
  {
    title: 'Email & CRM',
    description: 'Email routing, classification, and CRM notes.',
    mappings: [
      { uiField: 'Emails', source: 'Gmail Sync', table: 'synced_emails_v2', notes: 'Synced from Gmail API' },
      { uiField: 'Routing Status', source: 'Email Pipeline', table: 'email_classification_results', notes: 'AI-matched to customer' },
      { uiField: 'Classification', source: 'Email Pipeline', table: 'email_classification_results', notes: 'LLM category, confidence, summary' },
      { uiField: 'Personal Notes', source: 'CRM', table: 'personal_notes', notes: 'Manual entries by sales reps' },
      { uiField: 'Account Updates', source: 'CRM', table: 'account_updates', notes: 'Trip reports, meeting notes, etc. Note: trip report companies are often prospective customers not yet in Sage' },
    ],
  },
];

function SourceBadge({ source }: { source: string }) {
  let className = styles.sourceBadge;
  if (source.includes('Sage')) className += ` ${styles.sourceSage}`;
  else if (source.includes('chemmas_sales')) className += ` ${styles.sourceSalesCopilot}`;
  else if (source.includes('Sales Metrics')) className += ` ${styles.sourceSalesCopilot}`;
  else if (source.includes('Email') || source.includes('Gmail')) className += ` ${styles.sourceEmail}`;
  else if (source === 'Calculated') className += ` ${styles.sourceCalculated}`;
  else if (source === 'Internal' || source === 'CRM') className += ` ${styles.sourceInternal}`;
  else className += ` ${styles.sourceOther}`;

  return <span className={className}>{source}</span>;
}

export default function DataSourcesTab() {
  return (
    <div className={styles.container}>
      <div className={styles.intro}>
        <p className={styles.introText}>
          Reference mapping of every data field displayed in the dashboard and customer pages
          to its source system and database table. Use this to verify data correctness and trace
          data lineage.
        </p>
        <div className={styles.legend}>
          <span className={styles.legendLabel}>Sources:</span>
          <SourceBadge source="Sage ERP" />
          <SourceBadge source="chemmas_sales" />
          <SourceBadge source="Email Pipeline" />
          <SourceBadge source="Internal" />
          <SourceBadge source="Calculated" />
        </div>
      </div>

      {DATA_SECTIONS.map((section) => (
        <div key={section.title} className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>{section.title}</h3>
            <p className={styles.sectionDesc}>{section.description}</p>
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>UI Field</th>
                  <th>Source System</th>
                  <th>DB Table</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {section.mappings.map((mapping) => (
                  <tr key={mapping.uiField}>
                    <td className={styles.fieldName}>{mapping.uiField}</td>
                    <td><SourceBadge source={mapping.source} /></td>
                    <td><code className={styles.tableName}>{mapping.table}</code></td>
                    <td className={styles.notes}>{mapping.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
